import { useCallback, useContext, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import type { SocketNamespace } from '@trinity/chat';
import {
  WebsocketConfigContext,
  SessionManagerContext,
} from '@trinity/chat-components/containers/Chat/contexts';
import { getSocket } from '../socket';

export const useGetSocket = (namespace: SocketNamespace): (() => Socket) => {
  const socketOptions = useContext(WebsocketConfigContext);
  const sessionManager = useContext(SessionManagerContext);

  const sessionId = sessionManager?.sessionId;

  useEffect(() => {
    if (!sessionManager) {
      console.debug('[WS Session] No session manager available');
      return;
    }

    // Get current session or create one if needed
    if (!sessionId) {
      console.debug('[WS Session] No session ID, refreshing session...');
      sessionManager
        .refreshSession()
        .then(() => {
          console.debug('[WS Session] Session refreshed successfully');
        })
        .catch((error) => {
          console.error('[WS Session] Failed to initialize session for socket:', error);
        });
    } else {
      console.debug(`[WS Session] Session ID already available: ${sessionId.substring(0, 8)}...`);
    }
  }, [sessionId, sessionManager]);

  // Use a ref so the callback stays stable across sessionId changes.
  // The socket is created on call, so it always captures the latest sessionId
  // without the callback identity changing on every session refresh.
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  return useCallback(() => {
    return getSocket({
      namespace,
      baseUrl: socketOptions.baseUrl,
      socketPath: socketOptions.socketPath,
      sessionId: sessionIdRef.current,
    });
  }, [namespace, socketOptions]); // sessionId intentionally excluded — read from ref
};
